import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  billingAccountMsg,
  elisaOmaLaitelaskuMsg,
  emailMsg,
  inUseMsg,
  invoiceDeliveryMethodMsg,
  payerMsg,
  redeemableDeviceMsg,
  t,
} from '../../common/i18n/index.js';
import { EppRedeemTerminateRequestType } from '../../generated/api/models.js';
import { RedemptionPriceTitle, getDeviceCommitmentFields } from '../EoeSubscriptionDetails/eppSubscriptionUtils.js';
import { BillingAccountSelection } from './EppRedeemBillingAccountSelection.js';
import { getRedeemPrice } from './EppRedeemUtils.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { Subscription } from '../../generated/api/models.js';

type RedeemOptionDetailsProps = {
  billingAccountId?: string;
  billingAccountDisplayId?: string;
  subscription: Subscription;
  selectedOption: EppRedeemTerminateRequestType;
  twoColumnView?: boolean;
  additionalFields?: CLDescriptionItem[];
  companyName?: string;
  setBillingAccountId?: (billingAccountId: string) => void;
  setBillingAccountDisplayId?: (billingAccountDisplayId: string) => void;
};

const { COMPANY_REDEEM, EMPLOYEE_REDEEM, EMPLOYEE_REDEEM_BY_ADMIN } = EppRedeemTerminateRequestType;

export const RedeemOptionDetails = ({
  billingAccountId,
  billingAccountDisplayId,
  subscription,
  selectedOption,
  twoColumnView,
  additionalFields = [],
  companyName,
  setBillingAccountId,
  setBillingAccountDisplayId,
}: RedeemOptionDetailsProps) => {
  const omaLaiteLaskuItem =
    selectedOption === EMPLOYEE_REDEEM_BY_ADMIN
      ? { title: elisaOmaLaitelaskuMsg, description: t.V34H(inUseMsg) }
      : undefined;

  const getPayerItem = (): CLDescriptionItem | undefined => {
    switch (selectedOption) {
      case COMPANY_REDEEM:
        return { title: t.PB6S(payerMsg), description: companyName };
      case EMPLOYEE_REDEEM_BY_ADMIN:
        return { title: t.PB6S(payerMsg), description: subscription.subscriptionContactName };
      default:
        return undefined;
    }
  };

  const invoiceDeliveryItem =
    selectedOption === EMPLOYEE_REDEEM
      ? { title: t.H272(invoiceDeliveryMethodMsg), description: t.OKYY(emailMsg) }
      : undefined;

  const getBillingAccountItem = (): CLDescriptionItem | undefined => {
    if (selectedOption !== COMPANY_REDEEM) {
      return undefined;
    }

    return billingAccountDisplayId && billingAccountId
      ? {
          title: t.IFT9(billingAccountMsg),
          description: (
            <BillingAccountSelection
              billingAccountDisplayId={billingAccountDisplayId}
              billingAccountId={billingAccountId}
              setBillingAccountId={setBillingAccountId}
              setBillingAccountDisplayId={setBillingAccountDisplayId}
            />
          ),
        }
      : undefined;
  };

  const items: CLDescriptionItem[] = [
    { title: t.UDSQ(redeemableDeviceMsg), description: subscription.subscriptionName },
    ...getDeviceCommitmentFields(subscription),
    {
      title: <RedemptionPriceTitle />,
      description: getRedeemPrice(selectedOption, subscription.details!.device!.eppModificationPrices!),
    },
    omaLaiteLaskuItem,
    getPayerItem(),
    invoiceDeliveryItem,
    getBillingAccountItem(),
    ...additionalFields,
  ].filter((item): item is CLDescriptionItem => Boolean(item?.description));

  return <CL.Description className={dsClass.MARGIN_0} columns={twoColumnView ? 2 : 1} items={items} />;
};
